import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import {
  campaignAPI,
  adUnitAPI,
  inventoryAPI,
  trackingAPI,
} from "../services/api";
import "../styles/Dashboard.css";
import CampaignForm from "../components/CampaignForm";
import AdUnitForm from "../components/AdUnitForm";
import AccountSelector from "../components/AccountSelector";
import Modal from "../components/Modal";
import UiIcon from "../components/UiIcon";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { getApiErrorMessage } from "../utils/apiError";
import { getDuplicateDateWindow } from "../utils/editorState";
import {
  getCampaignIdsForAutomaticExpansion,
  getCampaignTitleSearchRows,
} from "../utils/campaignSearch";
import { sortAlphabetically } from "../utils/listOrdering";
import { belongsToChannelReport } from "../utils/reportMembership";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

const formatDateInput = (date) => {
  const dateValue = new Date(date);
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");
  const day = String(dateValue.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 29);

  return {
    startDate: formatDateInput(startDate),
    endDate: formatDateInput(endDate),
  };
};

const formatNumber = (value) => {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
};

const buildDailyChartData = (daily = []) => ({
  labels: daily.map((entry) =>
    new Date(entry.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
  ),
  datasets: [
    {
      label: "Impressions",
      data: daily.map((entry) => entry.impressions || 0),
      borderColor: "#6f98a6",
      backgroundColor: "rgba(111, 152, 166, 0.72)",
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
      maxBarThickness: 32,
    },
    {
      label: "Clicks",
      data: daily.map((entry) => entry.clicks || 0),
      borderColor: "#1f2b32",
      backgroundColor: "rgba(31, 43, 50, 0.78)",
      borderWidth: 1,
      borderRadius: 4,
      borderSkipped: false,
      maxBarThickness: 32,
    },
  ],
});

const getAdUnitInventoryIds = (adUnit) => {
  const ids = new Set();
  if (adUnit?.inventory) {
    ids.add(String(adUnit.inventory?._id || adUnit.inventory));
  }
  (Array.isArray(adUnit?.inventories) ? adUnit.inventories : []).forEach(
    (inventory) => ids.add(String(inventory?._id || inventory)),
  );
  return ids;
};

const formatTableDate = (date) => {
  if (!date) return "—";
  const dateValue = new Date(date);
  return Number.isNaN(dateValue.getTime())
    ? "—"
    : dateValue.toLocaleDateString();
};

const parseTableDate = (startDate, endDate) => {
  const startValue = startDate ? new Date(startDate).getTime() : NaN;
  if (Number.isFinite(startValue)) return startValue;
  const endValue = endDate ? new Date(endDate).getTime() : NaN;
  return Number.isFinite(endValue) ? endValue : null;
};

const getNumericMetric = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getAdChannelNamesFromAdUnits = (adUnits = []) => {
  const channelMap = new Map();

  (Array.isArray(adUnits) ? adUnits : []).forEach((adUnit) => {
    const linkedChannels = [
      ...(Array.isArray(adUnit?.inventories) ? adUnit.inventories : []),
      adUnit?.inventory,
    ];

    linkedChannels.forEach((channel) => {
      if (!channel || typeof channel !== "object" || !channel.name) {
        return;
      }

      const key = String(
        channel._id || channel.id || channel.name,
      ).toLowerCase();
      if (!channelMap.has(key)) {
        channelMap.set(key, channel.name);
      }
    });
  });

  return Array.from(channelMap.values());
};

const getCampaignAdChannelNames = (campaign) =>
  getAdChannelNamesFromAdUnits(
    Array.isArray(campaign?.adUnits) ? campaign.adUnits : [],
  );

const getAdUnitAdChannelNames = (adUnit) =>
  getAdChannelNamesFromAdUnits([adUnit]);

const hasAdUnitPreview = (adUnit) => Boolean(
  adUnit?.imageUrl || adUnit?.hasImageCreative,
);

const getCampaignPreviewAdUnit = (campaign) => {
  const adUnits = Array.isArray(campaign?.adUnits) ? campaign.adUnits : [];
  return adUnits.find((adUnit) => hasAdUnitPreview(adUnit)) || null;
};

function CampaignTablePreview({
  adUnit,
  className = "",
  deferUntilVisible = false,
}) {
  const directPreviewSource = adUnit?.imageUrl || null;
  const [previewSource, setPreviewSource] = useState(directPreviewSource);
  const [canLoadPreview, setCanLoadPreview] = useState(
    !deferUntilVisible || Boolean(directPreviewSource),
  );
  const previewRef = useRef(null);

  useEffect(() => {
    if (!deferUntilVisible || directPreviewSource) {
      setCanLoadPreview(true);
    }
  }, [deferUntilVisible, directPreviewSource]);

  useEffect(() => {
    if (!deferUntilVisible || canLoadPreview) return undefined;

    const previewElement = previewRef.current;
    if (!previewElement || typeof IntersectionObserver === "undefined") {
      setCanLoadPreview(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCanLoadPreview(true);
          observer.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    observer.observe(previewElement);
    return () => observer.disconnect();
  }, [canLoadPreview, deferUntilVisible]);

  useEffect(() => {
    if (directPreviewSource) {
      setPreviewSource(directPreviewSource);
      return undefined;
    }

    if (!canLoadPreview || !adUnit?._id || !adUnit?.hasImageCreative) {
      setPreviewSource(null);
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = null;
    let isCurrent = true;

    adUnitAPI.getCreative(adUnit._id, controller.signal)
      .then((response) => {
        if (!isCurrent || !response.data) return;
        objectUrl = URL.createObjectURL(response.data);
        setPreviewSource(objectUrl);
      })
      .catch((error) => {
        if (error?.code !== "ERR_CANCELED" && isCurrent) {
          setPreviewSource(null);
        }
      });

    return () => {
      isCurrent = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    adUnit?._id,
    adUnit?.hasImageCreative,
    canLoadPreview,
    directPreviewSource,
  ]);

  const previewClassName = ["campaign-table-preview", className]
    .filter(Boolean)
    .join(" ");

  return previewSource ? (
    <img
      ref={previewRef}
      className={previewClassName}
      src={previewSource}
      alt=""
      aria-hidden="true"
      loading="lazy"
    />
  ) : (
    <span
      ref={previewRef}
      className={`campaign-table-preview-empty${className ? ` ${className}` : ""}`}
    >
      No preview
    </span>
  );
}

function CampaignTableNameCell({ campaign, onOpen }) {
  const adChannelNames = getCampaignAdChannelNames(campaign);
  const previewAdUnit = getCampaignPreviewAdUnit(campaign);

  return (
    <td className="campaign-table-name">
      <div className="campaign-table-adunit-cell">
        <CampaignTablePreview adUnit={previewAdUnit} />
        <div className="campaign-table-adunit-copy">
          <span className="campaign-table-row-label">Campaign</span>
          <button
            type="button"
            className="campaign-table-name-button"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(campaign);
            }}
          >
            {campaign.name}
          </button>
          <span
            className="campaign-table-channel-line"
            title={`Ad Channels: ${adChannelNames.length > 0 ? adChannelNames.join(", ") : "None"}`}
          >
            Ad Channels:{" "}
            {adChannelNames.length > 0 ? adChannelNames.join(", ") : "None"}
          </span>
        </div>
      </div>
    </td>
  );
}

function AdUnitTableNameCell({ adUnit, campaignId, onOpen }) {
  const adChannelNames = getAdUnitAdChannelNames(adUnit);

  return (
    <td className="campaign-table-name campaign-table-adunit-name">
      <div className="campaign-table-adunit-cell">
        <CampaignTablePreview adUnit={adUnit} />
        <div className="campaign-table-adunit-copy">
          <span className="campaign-table-row-label">AdUnit</span>
          <button
            type="button"
            className="campaign-table-name-button campaign-table-adunit-name-button"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(adUnit, campaignId);
            }}
          >
            {adUnit.name || "Untitled Ad Unit"}
          </button>
          <span
            className="campaign-table-channel-line"
            title={`Ad Channels: ${adChannelNames.length > 0 ? adChannelNames.join(", ") : "None"}`}
          >
            Ad Channels:{" "}
            {adChannelNames.length > 0 ? adChannelNames.join(", ") : "None"}
          </span>
        </div>
      </div>
    </td>
  );
}

const CAMPAIGN_TABLE_SORT_COLUMNS = [
  { key: "startDate", label: "Start / End" },
  { key: "name", label: "Name" },
  { key: "impressions", label: "Total Impressions" },
  { key: "impressionsToday", label: "Impressions Today" },
  { key: "clicks", label: "Clicks" },
  { key: "clicksToday", label: "Clicks Today" },
  { key: "ctr", label: "CTR" },
];

const CAMPAIGN_PAGE_SIZE = 5;

function Dashboard({ view = "overview", searchQuery = "" }) {
  const { currentAccount, user, accounts } = useAuth();
  const isCampaignView = view === "campaigns";
  const isOverviewView = !isCampaignView;
  const normalizedSearchQuery = (searchQuery || "").trim();
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    normalizedSearchQuery,
  );
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignHasMore, setCampaignHasMore] = useState(true);
  const [campaignsLoadingMore, setCampaignsLoadingMore] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [overviewCampaignOptions, setOverviewCampaignOptions] = useState([]);
  const [overviewAdUnitOptions, setOverviewAdUnitOptions] = useState([]);
  const [overviewMode, setOverviewMode] = useState("campaign");
  const [selectedOverviewCampaignId, setSelectedOverviewCampaignId] =
    useState("");
  const [selectedOverviewAdUnitId, setSelectedOverviewAdUnitId] = useState("");
  const [selectedOverviewAdChannelId, setSelectedOverviewAdChannelId] =
    useState("");
  const [campaignFilterSearch, setCampaignFilterSearch] = useState("");
  const [adUnitFilterSearch, setAdUnitFilterSearch] = useState("");
  const [adChannelFilterSearch, setAdChannelFilterSearch] = useState("");
  const [isCampaignFilterOpen, setIsCampaignFilterOpen] = useState(false);
  const [isAdUnitFilterOpen, setIsAdUnitFilterOpen] = useState(false);
  const [isAdChannelFilterOpen, setIsAdChannelFilterOpen] = useState(false);
  const [selectedInventoryId, setSelectedInventoryId] = useState("");
  const [campaignSort, setCampaignSort] = useState({
    key: "name",
    direction: "asc",
    userSelected: false,
  });
  const [campaignEditorId, setCampaignEditorId] = useState(null);
  const [campaignEditorStatus, setCampaignEditorStatus] = useState(null);
  const [campaignSaveRevision, setCampaignSaveRevision] = useState(0);
  const [adUnitSaveRevision, setAdUnitSaveRevision] = useState(0);
  const [campaignDraftDirty, setCampaignDraftDirty] = useState(false);
  const [campaignStatusUpdatingId, setCampaignStatusUpdatingId] =
    useState(null);
  const [expandedCampaignIds, setExpandedCampaignIds] = useState(
    () => new Set(),
  );
  const [searchExpandedCampaignIds, setSearchExpandedCampaignIds] = useState(
    () => new Set(),
  );
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [analytics, setAnalytics] = useState({
    impressions: 0,
    clicks: 0,
    ctr: 0,
    daily: [],
    adUnitDaily: [],
    topCampaigns: [],
    topAdUnits: [],
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const selectedCampaignRef = useRef(null);
  const campaignPageRef = useRef(1);
  const campaignRequestIdRef = useRef(0);
  const campaignAbortControllerRef = useRef(null);
  const campaignRowsRef = useRef([]);
  const editorRequestRef = useRef(0);
  const loadedCampaignAccountRef = useRef(null);
  const analyticsRequestIdRef = useRef(0);
  const analyticsAbortControllerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const campaignFilterRef = useRef(null);
  const adUnitFilterRef = useRef(null);
  const adChannelFilterRef = useRef(null);
  const [loading, setLoading] = useState(isCampaignView);
  const [campaignsRefreshing, setCampaignsRefreshing] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showAdUnitModal, setShowAdUnitModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editingAdUnit, setEditingAdUnit] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingCampaignAction, setPendingCampaignAction] = useState(null);
  const [pendingAdUnitAction, setPendingAdUnitAction] = useState(null);
  const campaignMutationBusy = submitting || Boolean(pendingCampaignAction)
    || Boolean(campaignStatusUpdatingId) || Boolean(pendingAdUnitAction);
  const { notifyError: setError, notifySuccess: setSuccessMessage } = useToast();
  const confirmAction = useConfirm();

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearchQuery(normalizedSearchQuery),
      300,
    );
    return () => clearTimeout(timer);
  }, [normalizedSearchQuery]);

  const fetchCampaigns = useCallback(
    async ({
      page = 1,
      reset = false,
      silent = false,
      preservePageCount = false,
    } = {}) => {
      const requestId = campaignRequestIdRef.current + 1;
      campaignRequestIdRef.current = requestId;
      campaignAbortControllerRef.current?.abort();
      const controller = new AbortController();
      campaignAbortControllerRef.current = controller;
      const accountId = currentAccount?.id || null;
      const isInitialLoad = loadedCampaignAccountRef.current !== accountId;

      try {
        if (!accountId) {
          setCampaigns([]);
          setCampaignHasMore(false);
          setCampaignPage(1);
          setLoading(false);
          return;
        }

        if (reset && isInitialLoad && !silent) {
          setLoading(true);
        } else if (reset) {
          setCampaignsRefreshing(true);
        } else {
          setCampaignsLoadingMore(true);
        }

        const fetchPage = async (requestedPage) => {
          const params = {
            page: requestedPage,
            limit: CAMPAIGN_PAGE_SIZE,
            view: "summary",
            sortBy: campaignSort.key,
            sortDirection: campaignSort.direction,
          };
          if (selectedInventoryId) params.inventoryId = selectedInventoryId;
          if (debouncedSearchQuery) {
            params.search = debouncedSearchQuery;
            params.searchScope = "title";
          }
          return campaignAPI.getAll(params, { signal: controller.signal });
        };

        const pageCount = preservePageCount
          ? Math.max(1, campaignPageRef.current)
          : 1;
        const responses = preservePageCount
          ? await Promise.all(
              Array.from({ length: pageCount }, (_, index) => fetchPage(index + 1)),
            )
          : [await fetchPage(page)];

        if (campaignRequestIdRef.current !== requestId) return;

        const rowsByResponse = responses.map((response) =>
          Array.isArray(response.data?.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [],
        );
        const campaignRows = rowsByResponse.flat();
        const finalResponse = responses[responses.length - 1];
        const finalRows = rowsByResponse[rowsByResponse.length - 1];
        const hasMore =
          typeof finalResponse.data?.pagination?.hasMore === "boolean"
            ? finalResponse.data.pagination.hasMore
            : finalRows.length >= CAMPAIGN_PAGE_SIZE;

        const source = reset ? campaignRows : [...campaignRowsRef.current, ...campaignRows];
        const dedupedRows = Array.from(new Map(source.map((campaign) => [campaign._id, campaign])).values());
        campaignRowsRef.current = dedupedRows;
        setCampaigns(dedupedRows);
        setCampaignHasMore(Boolean(hasMore));
        const resultingPage = preservePageCount ? pageCount : page;
        campaignPageRef.current = resultingPage;
        setCampaignPage(resultingPage);
        loadedCampaignAccountRef.current = accountId;

        const automaticExpansionCampaignIds = getCampaignIdsForAutomaticExpansion(
          campaignRows,
          {
            searchQuery: debouncedSearchQuery,
            hasAdChannelFilter: Boolean(selectedInventoryId),
          },
        );
        setSearchExpandedCampaignIds((previousIds) => {
          if (reset) {
            return automaticExpansionCampaignIds;
          }
          if (!debouncedSearchQuery && !selectedInventoryId) {
            return new Set();
          }
          return new Set([...previousIds, ...automaticExpansionCampaignIds]);
        });

        const nextSelectedCampaignId = dedupedRows.some(
          (campaign) => campaign._id === selectedCampaignRef.current,
        )
          ? selectedCampaignRef.current
          : dedupedRows[0]?._id || null;

        selectedCampaignRef.current = nextSelectedCampaignId;
        setSelectedCampaign(nextSelectedCampaignId);
      } catch (error) {
        if (campaignRequestIdRef.current !== requestId) return;
        if (error?.code === "ERR_CANCELED") return;
        console.error("Error fetching campaigns:", error);
        setError("Failed to refresh campaign data. The previous data is still displayed.");
      } finally {
        if (campaignRequestIdRef.current === requestId) {
          setLoading(false);
          setCampaignsRefreshing(false);
          setCampaignsLoadingMore(false);
        }
      }
    },
    [
      currentAccount?.id,
      selectedInventoryId,
      debouncedSearchQuery,
      campaignSort.key,
      campaignSort.direction,
      setError,
    ],
  );

  useEffect(() => () => campaignAbortControllerRef.current?.abort(), []);

  useEffect(() => {
    if (!isCampaignView) {
      setLoading(false);
      return;
    }

    fetchCampaigns({ page: 1, reset: true });
  }, [fetchCampaigns, isCampaignView]);

  const loadMoreCampaigns = useCallback(() => {
    if (
      loading ||
      campaignsRefreshing ||
      campaignsLoadingMore ||
      !campaignHasMore ||
      !isCampaignView
    ) {
      return;
    }

    fetchCampaigns({ page: campaignPage + 1, reset: false });
  }, [
    loading,
    campaignsRefreshing,
    campaignsLoadingMore,
    campaignHasMore,
    isCampaignView,
    fetchCampaigns,
    campaignPage,
  ]);

  useEffect(() => {
    if (!isCampaignView || !campaignHasMore) {
      return undefined;
    }

    const target = loadMoreRef.current;
    if (!target) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadMoreCampaigns();
          }
        });
      },
      { rootMargin: "240px 0px 240px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isCampaignView, campaignHasMore, loadMoreCampaigns, campaigns.length]);

  const fetchAnalytics = useCallback(async () => {
    const requestId = analyticsRequestIdRef.current + 1;
    analyticsRequestIdRef.current = requestId;
    analyticsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    analyticsAbortControllerRef.current = controller;

    try {
      if (!dateRange.startDate || !dateRange.endDate || dateRange.startDate > dateRange.endDate) {
        setAnalyticsError("Choose a valid date range: Start Date must be on or before End Date.");
        return;
      }
      if (!currentAccount?.id) {
        setAnalytics({
          impressions: 0,
          clicks: 0,
          ctr: 0,
          daily: [],
          adUnitDaily: [],
          topCampaigns: [],
          topAdUnits: [],
        });
        setAnalyticsError("");
        setAnalyticsLoading(false);
        return;
      }

      setAnalyticsError("");
      setAnalyticsLoading(true);
      const response = await trackingAPI.getAnalytics({
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        inventoryId: selectedOverviewAdChannelId || undefined,
        includeReportItems: selectedOverviewAdChannelId ? true : undefined,
        campaignId:
          overviewMode === "campaign"
            ? selectedOverviewCampaignId || undefined
            : undefined,
        adUnitId:
          overviewMode === "adUnit"
            ? selectedOverviewAdUnitId || undefined
            : undefined,
        search: debouncedSearchQuery || undefined,
        searchScope: debouncedSearchQuery ? "adUnit" : undefined,
        includeAdUnitDaily:
          overviewMode === "campaign" && selectedOverviewCampaignId
            ? true
            : undefined,
        limit: 200,
      }, { signal: controller.signal });
      if (analyticsRequestIdRef.current !== requestId) return;
      setAnalytics({
        impressions: response.data?.impressions || 0,
        clicks: response.data?.clicks || 0,
        ctr: response.data?.ctr || 0,
        daily: response.data?.daily || [],
        adUnitDaily: response.data?.adUnitDaily || [],
        topCampaigns: response.data?.topCampaigns || [],
        topAdUnits: response.data?.topAdUnits || [],
      });
      setAnalyticsError("");
    } catch (error) {
      if (analyticsRequestIdRef.current !== requestId) return;
      if (error?.code === "ERR_CANCELED") return;
      console.error("Error fetching analytics:", error);
      setAnalyticsError(
        "Analytics could not be refreshed. The last successful report remains visible.",
      );
      setError("Failed to refresh analytics. The previous report is still displayed.");
    } finally {
      if (analyticsRequestIdRef.current === requestId) {
        setAnalyticsLoading(false);
      }
    }
  }, [
    currentAccount?.id,
    dateRange.endDate,
    dateRange.startDate,
    selectedOverviewAdChannelId,
    selectedOverviewCampaignId,
    selectedOverviewAdUnitId,
    overviewMode,
    debouncedSearchQuery,
    setError,
  ]);

  useEffect(() => () => analyticsAbortControllerRef.current?.abort(), []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    const controller = new AbortController();
    const loadInventories = async () => {
      try {
        const response = await inventoryAPI.getAll(undefined, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setInventories(response.data || []);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Error fetching inventories:", error);
        setError("Ad channel filters could not be loaded. Reload the page to retry.");
      }
    };

    setCampaigns([]);
    campaignRowsRef.current = [];
    editorRequestRef.current += 1;
    setEditingCampaign(null);
    setEditingAdUnit(null);
    setShowAdUnitModal(false);
    setShowCampaignModal(false);
    setInventories([]);
    setCampaignPage(1);
    campaignPageRef.current = 1;
    setCampaignHasMore(true);
    setCampaignsLoadingMore(false);
    selectedCampaignRef.current = null;
    setSelectedCampaign(null);
    setOverviewCampaignOptions([]);
    setOverviewAdUnitOptions([]);
    setOverviewMode("campaign");
    setSelectedOverviewCampaignId("");
    setSelectedOverviewAdUnitId("");
    setSelectedOverviewAdChannelId("");
    setCampaignFilterSearch("");
    setAdUnitFilterSearch("");
    setAdChannelFilterSearch("");
    setIsCampaignFilterOpen(false);
    setIsAdUnitFilterOpen(false);
    setIsAdChannelFilterOpen(false);
    setSelectedInventoryId("");
    setCampaignSort({
      key: "name",
      direction: "asc",
      userSelected: false,
    });
    setCampaignEditorId(null);
    setCampaignEditorStatus(null);
    setCampaignStatusUpdatingId(null);
    setPendingCampaignAction(null);
    setPendingAdUnitAction(null);
    setExpandedCampaignIds(new Set());
    setDateRange(getDefaultDateRange());
    loadInventories();
    return () => controller.abort();
  }, [currentAccount?.id, setError]);

  useEffect(() => {
    const controller = new AbortController();
    const loadOverviewFilterOptions = async () => {
      try {
        const [campaignResponse, adUnitResponse] = await Promise.all([
          campaignAPI.getAll({ view: "summary" }, { signal: controller.signal }),
          adUnitAPI.getAll({ view: "summary" }, { signal: controller.signal }),
        ]);
        if (controller.signal.aborted) return;
        const rows = Array.isArray(campaignResponse.data?.data)
          ? campaignResponse.data.data
          : Array.isArray(campaignResponse.data)
            ? campaignResponse.data
            : [];
        setOverviewCampaignOptions(rows);
        setOverviewAdUnitOptions(
          Array.isArray(adUnitResponse.data) ? adUnitResponse.data : [],
        );
      } catch (loadError) {
        if (controller.signal.aborted) return;
        setError("Report filters could not be refreshed. Reload the page to retry.");
      }
    };

    if (!currentAccount?.id) {
      setOverviewCampaignOptions([]);
      setOverviewAdUnitOptions([]);
      setSelectedOverviewCampaignId("");
      setSelectedOverviewAdUnitId("");
      setSelectedOverviewAdChannelId("");
      setCampaignFilterSearch("");
      setAdChannelFilterSearch("");
      return;
    }

    loadOverviewFilterOptions();
    return () => controller.abort();
  }, [currentAccount?.id, setError]);

  const sortedOverviewCampaignOptions = useMemo(
    () =>
      [...overviewCampaignOptions].sort((a, b) =>
        (a?.name || a?._id || "").localeCompare(
          b?.name || b?._id || "",
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [overviewCampaignOptions],
  );

  const sortedOverviewAdUnitOptions = useMemo(
    () =>
      [...overviewAdUnitOptions].sort((a, b) =>
        (a?.name || a?._id || "").localeCompare(
          b?.name || b?._id || "",
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [overviewAdUnitOptions],
  );

  const sortedAdChannelOptions = useMemo(
    () =>
      [...inventories].sort((a, b) =>
        (a?.name || a?._id || "").localeCompare(
          b?.name || b?._id || "",
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [inventories],
  );

  const filteredCampaignOptions = useMemo(() => {
    const searchValue = campaignFilterSearch.trim().toLowerCase();
    if (!searchValue) return sortedOverviewCampaignOptions;
    return sortedOverviewCampaignOptions.filter((campaign) =>
      (campaign?.name || campaign?._id || "")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [campaignFilterSearch, sortedOverviewCampaignOptions]);

  const filteredAdUnitOptions = useMemo(() => {
    const searchValue = adUnitFilterSearch.trim().toLowerCase();
    if (!searchValue) return sortedOverviewAdUnitOptions;
    return sortedOverviewAdUnitOptions.filter((adUnit) =>
      (adUnit?.name || adUnit?._id || "")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [adUnitFilterSearch, sortedOverviewAdUnitOptions]);

  const filteredAdChannelOptions = useMemo(() => {
    const searchValue = adChannelFilterSearch.trim().toLowerCase();
    if (!searchValue) return sortedAdChannelOptions;
    return sortedAdChannelOptions.filter((adChannel) =>
      (adChannel?.name || adChannel?._id || "")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [adChannelFilterSearch, sortedAdChannelOptions]);

  const selectedOverviewCampaign = useMemo(
    () =>
      sortedOverviewCampaignOptions.find(
        (campaign) => campaign._id === selectedOverviewCampaignId,
      ) || null,
    [selectedOverviewCampaignId, sortedOverviewCampaignOptions],
  );

  const selectedOverviewAdUnit = useMemo(
    () =>
      sortedOverviewAdUnitOptions.find(
        (adUnit) => adUnit._id === selectedOverviewAdUnitId,
      ) || null,
    [selectedOverviewAdUnitId, sortedOverviewAdUnitOptions],
  );

  const selectedOverviewAdUnitCampaignName = useMemo(() => {
    const campaign = selectedOverviewAdUnit?.campaign;
    if (!campaign) return "";
    if (typeof campaign === "object" && campaign.name) return campaign.name;

    const campaignId = typeof campaign === "object" ? campaign._id : campaign;
    return (
      sortedOverviewCampaignOptions.find(
        (option) => String(option._id) === String(campaignId),
      )?.name || ""
    );
  }, [selectedOverviewAdUnit, sortedOverviewCampaignOptions]);

  const selectedOverviewAdChannel = useMemo(
    () =>
      sortedAdChannelOptions.find(
        (adChannel) => adChannel._id === selectedOverviewAdChannelId,
      ) || null,
    [selectedOverviewAdChannelId, sortedAdChannelOptions],
  );

  const hasSelectedOverviewItem =
    overviewMode === "campaign"
      ? Boolean(selectedOverviewCampaignId)
      : Boolean(selectedOverviewAdUnitId);

  const handleViewAllOverviewItems = () => {
    if (overviewMode === "campaign") {
      setSelectedOverviewCampaignId("");
      setCampaignFilterSearch("");
      setIsCampaignFilterOpen(false);
      return;
    }

    setSelectedOverviewAdUnitId("");
    setAdUnitFilterSearch("");
    setIsAdUnitFilterOpen(false);
  };

  useEffect(() => {
    if (!selectedOverviewCampaignId) return;
    const exists = sortedOverviewCampaignOptions.some(
      (campaign) => campaign._id === selectedOverviewCampaignId,
    );
    if (!exists) {
      setSelectedOverviewCampaignId("");
      setCampaignFilterSearch("");
    }
  }, [selectedOverviewCampaignId, sortedOverviewCampaignOptions]);

  useEffect(() => {
    if (!selectedOverviewAdUnitId) return;
    const exists = sortedOverviewAdUnitOptions.some(
      (adUnit) => adUnit._id === selectedOverviewAdUnitId,
    );
    if (!exists) {
      setSelectedOverviewAdUnitId("");
      setAdUnitFilterSearch("");
    }
  }, [selectedOverviewAdUnitId, sortedOverviewAdUnitOptions]);

  useEffect(() => {
    if (!selectedOverviewAdChannelId) return;
    const exists = sortedAdChannelOptions.some(
      (adChannel) => adChannel._id === selectedOverviewAdChannelId,
    );
    if (!exists) {
      setSelectedOverviewAdChannelId("");
      setAdChannelFilterSearch("");
    }
  }, [selectedOverviewAdChannelId, sortedAdChannelOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!campaignFilterRef.current?.contains(event.target))
        setIsCampaignFilterOpen(false);
      if (!adUnitFilterRef.current?.contains(event.target))
        setIsAdUnitFilterOpen(false);
      if (!adChannelFilterRef.current?.contains(event.target))
        setIsAdChannelFilterOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpenCreateModal = () => {
    editorRequestRef.current += 1;
    setEditingCampaign(null);
    setShowCampaignModal(true);
    setError(null);
  };

  const handleOpenCampaignEditor = async (campaign) => {
    if (pendingCampaignAction) return;
    const requestId = ++editorRequestRef.current;
    setPendingCampaignAction({ type: "load", id: campaign._id });
    try {
      const response = await campaignAPI.getById(campaign._id);
      if (editorRequestRef.current !== requestId) return;
      selectedCampaignRef.current = campaign._id;
      setSelectedCampaign(campaign._id);
      setEditingCampaign(response.data);
      setCampaignEditorId(campaign._id);
      setCampaignEditorStatus(response.data.status);
      setCampaignDraftDirty(false);
      setError(null);
    } catch (error) {
      if (editorRequestRef.current === requestId) setError(getApiErrorMessage(error, "Failed to load campaign editor"));
    } finally {
      if (editorRequestRef.current === requestId) setPendingCampaignAction(null);
    }
  };

  const refreshCampaignEditor = async (campaignId) => {
    const requestId = editorRequestRef.current;
    try {
      const response = await campaignAPI.getById(campaignId);
      if (requestId !== editorRequestRef.current) return;
      setEditingCampaign((previous) => previous?._id === campaignId
        ? { ...previous, adUnits: response.data.adUnits }
        : previous);
    } catch (error) {
      if (requestId === editorRequestRef.current) setError("The action was saved, but the editor could not refresh. Reopen it before the next action.");
    }
  };

  const handleCloseCampaignEditor = () => {
    editorRequestRef.current += 1;
    if (campaignEditorId && campaignEditorStatus) {
      setCampaigns((previousCampaigns) =>
        previousCampaigns.map((campaign) =>
          campaign._id === campaignEditorId
            ? { ...campaign, status: campaignEditorStatus }
            : campaign,
        ),
      );
    }
    setCampaignEditorId(null);
    setCampaignEditorStatus(null);
    setEditingCampaign(null);
    setError(null);
  };

  const handleCloseCampaignModal = () => {
    editorRequestRef.current += 1;
    setShowCampaignModal(false);
    setCampaignEditorId(null);
    setCampaignEditorStatus(null);
    setEditingCampaign(null);
    setError(null);
  };

  const handleOpenCreateAdUnitModal = (campaignId = null) => {
    editorRequestRef.current += 1;
    if (campaignId) {
      selectedCampaignRef.current = campaignId;
      setSelectedCampaign(campaignId);
    }
    setEditingAdUnit(null);
    setShowAdUnitModal(true);
    setError(null);
  };

  const confirmDiscardCampaignDraft = async () => !campaignDraftDirty || await confirmAction({
    title: "Unsaved campaign changes",
    message: "Save the Campaign first to keep your changes. Continue without saving?",
    confirmLabel: "Discard and continue",
  });

  const handleOpenCreateAdUnitFromCampaignEditor = async (campaignId) => {
    if (!await confirmDiscardCampaignDraft()) return;
    handleCloseCampaignEditor();
    handleOpenCreateAdUnitModal(campaignId);
  };

  const handleOpenEditAdUnitModal = async (
    adUnit,
    campaignId = null,
    closeCampaignEditor = false,
  ) => {
    if (pendingAdUnitAction) return;
    if (closeCampaignEditor && !await confirmDiscardCampaignDraft()) return;
    const requestId = ++editorRequestRef.current;
    setError(null);
    setPendingAdUnitAction({ type: "load", id: adUnit._id });
    try {
      const response = await adUnitAPI.getById(adUnit._id);
      if (requestId !== editorRequestRef.current) return;
      const detailedAdUnit = response.data;
      const resolvedCampaignId =
        campaignId ||
        detailedAdUnit?.campaign?._id ||
        detailedAdUnit?.campaign ||
        null;
      if (resolvedCampaignId) {
        selectedCampaignRef.current = resolvedCampaignId;
        setSelectedCampaign(resolvedCampaignId);
      }
      if (closeCampaignEditor) handleCloseCampaignEditor();
      setEditingAdUnit(detailedAdUnit);
      setShowAdUnitModal(true);
    } catch (err) {
      if (requestId !== editorRequestRef.current) return;
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to load ad unit details",
      );
    } finally {
      setPendingAdUnitAction((pending) => pending?.type === "load" && pending?.id === adUnit._id ? null : pending);
    }
  };

  const handleCloseAdUnitModal = () => {
    editorRequestRef.current += 1;
    setShowAdUnitModal(false);
    setEditingAdUnit(null);
    setError(null);
  };

  const handleSubmitCampaign = async (formData) => {
    if (campaignMutationBusy) return;
    const requestId = editorRequestRef.current;
    setSubmitting(true);
    setError(null);
    try {
      if (editingCampaign) {
        const response = await campaignAPI.update(editingCampaign._id, formData);
        if (requestId === editorRequestRef.current) {
          setEditingCampaign(response.data);
          setCampaignEditorStatus(response.data.status);
          setCampaignSaveRevision((revision) => revision + 1);
          setCampaignDraftDirty(false);
        }
        setSuccessMessage("Campaign updated successfully!");
      } else {
        await campaignAPI.create(formData);
        setSuccessMessage("Campaign created successfully!");
        if (requestId === editorRequestRef.current) handleCloseCampaignModal();
      }
      await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save campaign"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (campaignMutationBusy) return;
    const confirmed = await confirmAction({
      title: "Delete campaign?",
      message:
        "Only an empty campaign can be deleted. Move or delete all Ad Units first. This action cannot be undone.",
      confirmLabel: "Delete campaign",
    });
    if (!confirmed) return;

    try {
      setPendingCampaignAction({ type: "delete", id: campaignId });
      await campaignAPI.delete(campaignId);
      setSuccessMessage("Campaign deleted successfully!");
      if (campaignEditorId === campaignId) {
        handleCloseCampaignEditor();
      }
      await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
      if (selectedCampaign === campaignId) {
        selectedCampaignRef.current = null;
        setSelectedCampaign(null);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to delete campaign"));
    } finally {
      setPendingCampaignAction(null);
    }
  };

  const handleSubmitAdUnit = async (formData) => {
    if (submitting) return;
    const requestId = editorRequestRef.current;
    setSubmitting(true);
    setError(null);
    try {
      if (editingAdUnit) {
        const response = await adUnitAPI.update(editingAdUnit._id, formData);
        if (requestId === editorRequestRef.current) {
          setEditingAdUnit(response.data);
          setAdUnitSaveRevision((revision) => revision + 1);
        }
        setSuccessMessage("Ad unit updated successfully!");
      } else {
        await adUnitAPI.create(formData);
        setSuccessMessage("Ad unit created successfully!");
        if (requestId === editorRequestRef.current) handleCloseAdUnitModal();
      }
      if (isCampaignView) {
        await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
        if (campaignEditorId) await refreshCampaignEditor(campaignEditorId);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to save ad unit"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdUnit = async (adUnitId) => {
    if (pendingAdUnitAction) return;
    const confirmed = await confirmAction({
      title: "Delete ad unit?",
      message: "This ad unit will be permanently deleted. This action cannot be undone.",
      confirmLabel: "Delete ad unit",
    });
    if (!confirmed) return;

    try {
      setPendingAdUnitAction({ type: "delete", id: adUnitId });
      await adUnitAPI.delete(adUnitId);
      setSuccessMessage("Ad unit deleted successfully!");
      if (isCampaignView) {
        await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
        if (campaignEditorId) await refreshCampaignEditor(campaignEditorId);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to delete ad unit"));
    } finally {
      setPendingAdUnitAction(null);
    }
  };

  const handleToggleCampaignStatus = async (campaignId, currentStatus) => {
    if (campaignMutationBusy) return;

    const newStatus = currentStatus === "active" ? "paused" : "active";
    const requestId = editorRequestRef.current;
    setCampaignStatusUpdatingId(campaignId);
    try {
      const response = await campaignAPI.updateStatus(campaignId, newStatus);
      const savedStatus = response.data?.status || newStatus;
      if (requestId === editorRequestRef.current) setCampaignEditorStatus(savedStatus);
      setEditingCampaign((previous) => previous?._id === campaignId ? { ...previous, status: savedStatus, updatedAt: response.data.updatedAt } : previous);
      setCampaigns((previous) => previous.map((item) => item._id === campaignId ? { ...item, status: savedStatus } : item));
      setSuccessMessage(
        `Campaign ${savedStatus === "active" ? "activated" : "paused"} successfully!`,
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to update campaign status"));
    } finally {
      setCampaignStatusUpdatingId(null);
    }
  };

  const handleToggleAdUnitStatus = async (adUnitId, currentStatus) => {
    if (campaignMutationBusy) return;
    try {
      setPendingAdUnitAction({ type: "status", id: adUnitId });
      const newStatus = currentStatus === "active" ? "paused" : "active";
      await adUnitAPI.updateStatus(adUnitId, newStatus);
      setSuccessMessage(
        `Ad unit ${newStatus === "active" ? "activated" : "paused"} successfully!`,
      );
      if (isCampaignView) {
        await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
        if (campaignEditorId) await refreshCampaignEditor(campaignEditorId);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to update ad unit status"));
    } finally {
      setPendingAdUnitAction(null);
    }
  };

  const generateCopyName = (baseName, existingNames) => {
    const clean = baseName.trim();
    const copyBase = `${clean} (Copy)`;
    if (!existingNames.has(copyBase)) return copyBase;
    let i = 2;
    while (existingNames.has(`${clean} (Copy ${i})`)) {
      i += 1;
    }
    return `${clean} (Copy ${i})`;
  };

  const getDuplicateAdUnitDateWindow = (adUnit) => {
    const startDate = new Date();
    startDate.setMinutes(startDate.getMinutes() + 5);

    const sourceStart = new Date(adUnit.startDate);
    const sourceEnd = new Date(adUnit.endDate);
    const sourceDurationMs =
      Number.isNaN(sourceStart.getTime()) || Number.isNaN(sourceEnd.getTime())
        ? 24 * 60 * 60 * 1000
        : Math.max(sourceEnd.getTime() - sourceStart.getTime(), 24 * 60 * 60 * 1000);

    const campaignId = adUnit.campaign?._id || adUnit.campaign || selectedCampaign;
    const parentCampaign = campaigns.find((campaign) => campaign._id === campaignId);
    const campaignEnd = parentCampaign?.endDate ? new Date(parentCampaign.endDate) : null;
    const fallbackEnd = new Date(startDate.getTime() + sourceDurationMs);
    const endDate =
      campaignEnd && !Number.isNaN(campaignEnd.getTime()) && campaignEnd > startDate
        ? campaignEnd
        : fallbackEnd;

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  };

  const handleDuplicateCampaign = async (campaign) => {
    if (campaignMutationBusy) return;
    const duplicateDates = getDuplicateDateWindow(campaign);
    const confirmed = await confirmAction({
      title: "Duplicate campaign?",
      message: `The new Campaign will start ${new Date(duplicateDates.startDate).toLocaleString()} and end ${new Date(duplicateDates.endDate).toLocaleString()}. The original Campaign will not change.`,
      confirmLabel: "Create copy",
    });
    if (!confirmed) return;
    try {
      setPendingCampaignAction({ type: "duplicate", id: campaign._id });
      const existing = new Set(campaigns.map((c) => c.name));
      const name = generateCopyName(campaign.name, existing);
      await campaignAPI.create({
        name,
        description: campaign.description || "",
        ...duplicateDates,
      });
      setSuccessMessage("Campaign duplicated successfully!");
      await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to duplicate campaign"));
    } finally {
      setPendingCampaignAction(null);
    }
  };

  const handleDuplicateAdUnit = async (adUnit) => {
    if (pendingAdUnitAction) return;
    try {
      setPendingAdUnitAction({ type: "duplicate", id: adUnit._id });
      const response = await adUnitAPI.getById(adUnit._id);
      const detailedAdUnit = response.data;
      const duplicateDateWindow = getDuplicateAdUnitDateWindow(detailedAdUnit);
      const existingAdUnitNames = new Set(
        campaigns.flatMap((campaign) =>
          (Array.isArray(campaign.adUnits) ? campaign.adUnits : []).map(
            (campaignAdUnit) => campaignAdUnit.name,
          ),
        ),
      );
      const duplicateName = generateCopyName(
        detailedAdUnit.name,
        existingAdUnitNames,
      );
      await adUnitAPI.create({
        name: duplicateName,
        campaign:
          detailedAdUnit.campaign?._id ||
          detailedAdUnit.campaign ||
          selectedCampaign,
        inventory:
          detailedAdUnit.inventory?._id || detailedAdUnit.inventory,
        inventories: Array.isArray(detailedAdUnit.inventories)
          ? detailedAdUnit.inventories
              .map((inventory) => inventory?._id || inventory)
              .filter(Boolean)
          : undefined,
        startDate: duplicateDateWindow.startDate,
        endDate: duplicateDateWindow.endDate,
        imageUrl: detailedAdUnit.imageUrl,
        htmlCreative: detailedAdUnit.htmlCreative,
        iframeUrl: detailedAdUnit.iframeUrl,
        clickUrl: detailedAdUnit.clickUrl,
        width: detailedAdUnit.width,
      });
      setSuccessMessage("Ad unit duplicated successfully!");
      if (isCampaignView) {
        await fetchCampaigns({ reset: true, silent: true, preservePageCount: true });
        if (campaignEditorId) await refreshCampaignEditor(campaignEditorId);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to duplicate ad unit"));
    } finally {
      setPendingAdUnitAction(null);
    }
  };

  const sortedCampaigns = useMemo(() => {
    const directionMultiplier = campaignSort.direction === "asc" ? 1 : -1;

    const getSortValue = (row, key, isCampaign = false) => {
      switch (key) {
        case "startDate":
          return parseTableDate(row.startDate, row.endDate);
        case "name":
          return String(row.name || "").toLowerCase();
        case "impressions":
          return getNumericMetric(
            isCampaign ? row.totalImpressions : row.impressions,
          );
        case "impressionsToday":
          return getNumericMetric(row.impressionsToday);
        case "clicks":
          return getNumericMetric(isCampaign ? row.totalClicks : row.clicks);
        case "clicksToday":
          return getNumericMetric(row.clicksToday);
        case "ctr":
          return getNumericMetric(row.ctr);
        default:
          return parseTableDate(row.startDate || row.createdAt, row.endDate);
      }
    };

    const compareRows = (a, b, isCampaign = false) => {
      const aValue = getSortValue(a, campaignSort.key, isCampaign);
      const bValue = getSortValue(b, campaignSort.key, isCampaign);

      if (campaignSort.key === "startDate") {
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return 1;
        if (bValue === null) return -1;
      }

      if (typeof aValue === "string" || typeof bValue === "string") {
        return (
          String(aValue).localeCompare(String(bValue), undefined, {
            sensitivity: "base",
          }) * directionMultiplier
        );
      }

      return ((aValue || 0) - (bValue || 0)) * directionMultiplier;
    };

    return [...campaigns]
      .map((campaign) => ({
        ...campaign,
        adUnits: [
          ...(Array.isArray(campaign.adUnits) ? campaign.adUnits : []),
        ].sort((a, b) => compareRows(a, b, false)),
      }))
      .sort((a, b) => compareRows(a, b, true));
  }, [campaignSort, campaigns]);

  const handleCampaignTableSort = useCallback((key) => {
    setCampaignSort((prev) => ({
      key,
      direction:
        prev.key === key && prev.userSelected && prev.direction === "asc"
          ? "desc"
          : "asc",
      userSelected: true,
    }));
  }, []);

  const toggleCampaignAdUnits = useCallback((campaignId, isExpanded) => {
    setSearchExpandedCampaignIds((prev) => {
      if (!prev.has(campaignId)) return prev;
      const next = new Set(prev);
      next.delete(campaignId);
      return next;
    });
    setExpandedCampaignIds((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  }, []);

  const campaignEditorCampaign = useMemo(
    () => {
      if (!campaignEditorId) return null;
      const refreshedCampaign = campaigns.find(
        (campaign) => campaign._id === campaignEditorId,
      );
      if (editingCampaign?._id === campaignEditorId) return editingCampaign;
      return refreshedCampaign || null;
    },
    [campaignEditorId, campaigns, editingCampaign],
  );
  const effectiveCampaignEditorStatus =
    campaignEditorStatus || campaignEditorCampaign?.status || null;

  const analyticsChartData = useMemo(
    () => buildDailyChartData(analytics.daily),
    [analytics.daily],
  );

  const selectedCampaignAdUnitDailyReports = useMemo(() => {
    if (
      overviewMode !== "campaign" ||
      !selectedOverviewCampaignId ||
      !selectedOverviewCampaign
    ) {
      return [];
    }

    const dailyByAdUnitId = new Map();
    (Array.isArray(analytics.adUnitDaily) ? analytics.adUnitDaily : []).forEach(
      (entry) => {
        const adUnitId = String(entry.adUnitId || "");
        if (!adUnitId) return;
        if (!dailyByAdUnitId.has(adUnitId)) {
          dailyByAdUnitId.set(adUnitId, []);
        }
        dailyByAdUnitId.get(adUnitId).push(entry);
      },
    );

    return (Array.isArray(selectedOverviewCampaign.adUnits)
      ? selectedOverviewCampaign.adUnits
      : []
    )
      .map((adUnit) => {
        const daily = dailyByAdUnitId.get(String(adUnit._id)) || [];
        const totals = daily.reduce(
          (result, entry) => ({
            impressions: result.impressions + Number(entry.impressions || 0),
            clicks: result.clicks + Number(entry.clicks || 0),
          }),
          { impressions: 0, clicks: 0 },
        );

        return {
          adUnit,
          daily,
          impressions: totals.impressions,
          clicks: totals.clicks,
          ctr:
            totals.impressions > 0
              ? (totals.clicks / totals.impressions) * 100
              : 0,
        };
      })
      .sort((left, right) =>
        String(left.adUnit?.name || "").localeCompare(
          String(right.adUnit?.name || ""),
        ),
      );
  }, [
    analytics.adUnitDaily,
    overviewMode,
    selectedOverviewCampaign,
    selectedOverviewCampaignId,
  ]);

  const overviewBreakdownItems = useMemo(() => {
    const normalizedSearch = debouncedSearchQuery.toLowerCase();
    const metrics = new Map(
      (overviewMode === "campaign"
        ? analytics.topCampaigns
        : analytics.topAdUnits
      ).map((item) => [
        String(overviewMode === "campaign" ? item.campaignId : item.adUnitId),
        item,
      ]),
    );

    if (overviewMode === "campaign") {
      return sortedOverviewCampaignOptions
        .filter((campaign) =>
          !selectedOverviewCampaignId ||
          String(campaign._id) === String(selectedOverviewCampaignId),
        )
        .filter((campaign) => {
          const adUnits = Array.isArray(campaign.adUnits) ? campaign.adUnits : [];
          if (
            selectedOverviewAdChannelId &&
            !belongsToChannelReport(campaign._id, adUnits.some((adUnit) =>
              getAdUnitInventoryIds(adUnit).has(String(selectedOverviewAdChannelId)),
            ), metrics)
          ) {
            return false;
          }
          if (
            normalizedSearch &&
            !adUnits.some((adUnit) =>
              String(adUnit?.name || "").toLowerCase().includes(normalizedSearch),
            )
          ) {
            return false;
          }
          return true;
        })
        .map((campaign) => ({
          campaignId: campaign._id,
          name: campaign.name,
          previewAdUnit: getCampaignPreviewAdUnit(campaign),
          impressions: 0,
          clicks: 0,
          ctr: 0,
          ...(metrics.get(String(campaign._id)) || {}),
        }));
    }

    return sortedOverviewAdUnitOptions
      .filter((adUnit) =>
        !selectedOverviewAdUnitId ||
        String(adUnit._id) === String(selectedOverviewAdUnitId),
      )
      .filter(
        (adUnit) =>
          !selectedOverviewAdChannelId ||
          belongsToChannelReport(adUnit._id,
            getAdUnitInventoryIds(adUnit).has(String(selectedOverviewAdChannelId)), metrics),
      )
      .filter(
        (adUnit) =>
          !normalizedSearch ||
          String(adUnit?.name || "").toLowerCase().includes(normalizedSearch),
      )
      .map((adUnit) => ({
        adUnitId: adUnit._id,
        adCode: adUnit.adCode,
        name: adUnit.name,
        campaignName:
          adUnit.campaign && typeof adUnit.campaign === "object"
            ? adUnit.campaign.name
            : null,
        previewAdUnit: adUnit,
        impressions: 0,
        clicks: 0,
        ctr: 0,
        ...(metrics.get(String(adUnit._id)) || {}),
      }));
  }, [
    analytics.topAdUnits,
    analytics.topCampaigns,
    debouncedSearchQuery,
    overviewMode,
    selectedOverviewAdChannelId,
    selectedOverviewAdUnitId,
    selectedOverviewCampaignId,
    sortedOverviewAdUnitOptions,
    sortedOverviewCampaignOptions,
  ]);

  const analyticsChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            color: "#49545c",
            font: {
              family: "Rubik",
            },
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      scales: {
        x: {
          stacked: false,
          grid: {
            display: false,
          },
          ticks: {
            autoSkip: true,
            color: "#67757e",
            maxRotation: 0,
            maxTicksLimit: 12,
            font: {
              family: "Rubik",
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(124, 170, 184, 0.12)",
          },
          ticks: {
            color: "#67757e",
            font: {
              family: "Rubik",
            },
          },
        },
      },
    }),
    [],
  );

  const hasSearchNoDashboardData = useMemo(() => {
    if (!debouncedSearchQuery || analyticsLoading) return false;
    const impressions = Number(analytics?.impressions || 0);
    const clicks = Number(analytics?.clicks || 0);
    const dailyCount = Array.isArray(analytics?.daily)
      ? analytics.daily.length
      : 0;
    const topCampaignCount = Array.isArray(analytics?.topCampaigns)
      ? analytics.topCampaigns.length
      : 0;
    const topAdUnitCount = Array.isArray(analytics?.topAdUnits)
      ? analytics.topAdUnits.length
      : 0;
    return (
      impressions === 0 &&
      clicks === 0 &&
      dailyCount === 0 &&
      topCampaignCount === 0 &&
      topAdUnitCount === 0
    );
  }, [debouncedSearchQuery, analyticsLoading, analytics]);

  if (loading) return <div className="loading">Loading...</div>;

  if (
    user?.role === "editor" &&
    (!Array.isArray(accounts) || accounts.length === 0 || !currentAccount?.id)
  ) {
    return (
      <div className="dashboard">
        <header className="dashboard-header">
          <div>
            <h1>{isCampaignView ? "Campaigns" : "Dashboard"}</h1>
          </div>
        </header>
        <div className="no-selection">
          <p>
            No account has been shared with you yet. Please contact the account
            owner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header
        className={`dashboard-header${isCampaignView ? " campaigns-header-row" : ""}`}
      >
        <div>
          <h1>{isCampaignView ? "Campaigns" : "Overview"}</h1>
          {!isCampaignView ? (
            <p className="dashboard-header-copy">
              Check performance, narrow the date range, and keep campaign
              browsing straightforward.
            </p>
          ) : (
            <p className="dashboard-header-copy">
              Overview of delivery, engagement, and account-level performance.
            </p>
          )}
        </div>
        {isCampaignView ? (
          <div className="dashboard-header-actions campaigns-header-controls">
            <button
              className="btn btn-primary campaigns-new-button"
              onClick={handleOpenCreateModal}
            >
              <span aria-hidden="true">+</span> New Campaign
            </button>
            <div className="dashboard-filter-field campaigns-header-filter">
              <label
                htmlFor="campaign-inventory-filter"
                className="account-list-label"
              >
                Ad Channel Filter
              </label>
              <select
                id="campaign-inventory-filter"
                className="account-select dashboard-filter-select"
                value={selectedInventoryId}
                onChange={(e) => setSelectedInventoryId(e.target.value)}
              >
                <option value="">All Ad Channels</option>
                {sortedAdChannelOptions.map((inventory) => (
                  <option key={inventory._id} value={inventory._id}>
                    {inventory.name}
                  </option>
                ))}
              </select>
            </div>
            <AccountSelector />
            <span
              className={`background-refresh-status${campaignsRefreshing ? " is-visible" : ""}`}
              role="status"
              aria-live="polite"
            >
              Refreshing…
            </span>
          </div>
        ) : (
          <div className="dashboard-overview-controls">
            <div className="dashboard-overview-mode-wrap">
              <span className="account-list-label dashboard-overview-mode-label">
                Report View
              </span>
              <div className="dashboard-overview-mode" role="tablist" aria-label="Overview report type">
              <button
                type="button"
                role="tab"
                aria-selected={overviewMode === "campaign"}
                aria-label="Campaign Overview"
                title="Campaign Overview"
                className={overviewMode === "campaign" ? "is-active" : ""}
                onClick={() => {
                  setOverviewMode("campaign");
                  setSelectedOverviewAdUnitId("");
                  setAdUnitFilterSearch("");
                }}
              >
                Campaign
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={overviewMode === "adUnit"}
                aria-label="Ad Unit Overview"
                title="Ad Unit Overview"
                className={overviewMode === "adUnit" ? "is-active" : ""}
                onClick={() => {
                  setOverviewMode("adUnit");
                  setSelectedOverviewCampaignId("");
                  setCampaignFilterSearch("");
                }}
              >
                Ad Unit
              </button>
              </div>
            </div>
            <div className="dashboard-filter-grid">
              <div className="dashboard-filter-field">
                <label
                  htmlFor="dashboard-start-date"
                  className="account-list-label"
                >
                  Start Date
                </label>
                <input
                  id="dashboard-start-date"
                  type="date"
                  className="dashboard-date-input"
                  value={dateRange.startDate}
                  max={dateRange.endDate || undefined}
                  onChange={(e) =>
                    setDateRange((current) => ({
                      ...current,
                      startDate: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="dashboard-filter-field">
                <label
                  htmlFor="dashboard-end-date"
                  className="account-list-label"
                >
                  End Date
                </label>
                <input
                  id="dashboard-end-date"
                  type="date"
                  className="dashboard-date-input"
                  value={dateRange.endDate}
                  min={dateRange.startDate || undefined}
                  onChange={(e) =>
                    setDateRange((current) => ({
                      ...current,
                      endDate: e.target.value,
                    }))
                  }
                />
              </div>
              {overviewMode === "campaign" ? (
              <div
                className="dashboard-filter-field dashboard-filter-field-wide"
                ref={campaignFilterRef}
              >
                <label
                  htmlFor="dashboard-campaign-filter"
                  className="account-list-label"
                >
                  Campaign
                </label>
                <div className="dashboard-filter-combobox">
                  <input
                    id="dashboard-campaign-filter"
                    type="text"
                    className="account-select dashboard-filter-select dashboard-filter-input"
                    placeholder="All Campaigns"
                    value={campaignFilterSearch}
                    onFocus={() => setIsCampaignFilterOpen(true)}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setCampaignFilterSearch(nextValue);
                      setIsCampaignFilterOpen(true);
                      if (!nextValue.trim()) {
                        setSelectedOverviewCampaignId("");
                        return;
                      }
                      const selectedLabel =
                        selectedOverviewCampaign?.name ||
                        selectedOverviewCampaign?._id ||
                        "";
                      if (
                        selectedOverviewCampaignId &&
                        nextValue !== selectedLabel
                      ) {
                        setSelectedOverviewCampaignId("");
                      }
                    }}
                  />
                  {isCampaignFilterOpen && (
                    <div
                      className="dashboard-filter-options"
                      role="listbox"
                      aria-label="Campaign filter options"
                    >
                      <button
                        type="button"
                        className={`dashboard-filter-option${!selectedOverviewCampaignId ? " selected" : ""}`}
                        onClick={() => {
                          setSelectedOverviewCampaignId("");
                          setCampaignFilterSearch("");
                          setIsCampaignFilterOpen(false);
                        }}
                      >
                        All Campaigns
                      </button>
                      {filteredCampaignOptions.length > 0 ? (
                        filteredCampaignOptions.map((campaign) => (
                          <button
                            type="button"
                            key={campaign._id}
                            className={`dashboard-filter-option${selectedOverviewCampaignId === campaign._id ? " selected" : ""}`}
                            onClick={() => {
                              setSelectedOverviewCampaignId(campaign._id);
                              setCampaignFilterSearch(
                                campaign.name || campaign._id,
                              );
                              setIsCampaignFilterOpen(false);
                            }}
                          >
                            {campaign.name || campaign._id}
                          </button>
                        ))
                      ) : (
                        <div className="dashboard-filter-option-empty">
                          No campaigns found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <small className="dashboard-filter-helper">
                  {selectedOverviewCampaign
                    ? `Filtering by ${selectedOverviewCampaign.name || selectedOverviewCampaign._id}`
                    : "Showing all campaigns"}
                </small>
              </div>
              ) : (
                <div
                  className="dashboard-filter-field dashboard-filter-field-wide"
                  ref={adUnitFilterRef}
                >
                  <label htmlFor="dashboard-ad-unit-filter" className="account-list-label">
                    Ad Unit / Banner
                  </label>
                  <div className="dashboard-filter-combobox">
                    <input
                      id="dashboard-ad-unit-filter"
                      type="text"
                      className="account-select dashboard-filter-select dashboard-filter-input"
                      placeholder="All Ad Units"
                      value={adUnitFilterSearch}
                      onFocus={() => setIsAdUnitFilterOpen(true)}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setAdUnitFilterSearch(nextValue);
                        setIsAdUnitFilterOpen(true);
                        if (!nextValue.trim()) {
                          setSelectedOverviewAdUnitId("");
                          return;
                        }
                        const selectedLabel =
                          selectedOverviewAdUnit?.name || selectedOverviewAdUnit?._id || "";
                        if (selectedOverviewAdUnitId && nextValue !== selectedLabel) {
                          setSelectedOverviewAdUnitId("");
                        }
                      }}
                    />
                    {isAdUnitFilterOpen && (
                      <div className="dashboard-filter-options" role="listbox" aria-label="Ad Unit filter options">
                        <button
                          type="button"
                          className={`dashboard-filter-option${!selectedOverviewAdUnitId ? " selected" : ""}`}
                          onClick={() => {
                            setSelectedOverviewAdUnitId("");
                            setAdUnitFilterSearch("");
                            setIsAdUnitFilterOpen(false);
                          }}
                        >
                          All Ad Units
                        </button>
                        {filteredAdUnitOptions.length > 0 ? (
                          filteredAdUnitOptions.map((adUnit) => (
                            <button
                              type="button"
                              key={adUnit._id}
                              className={`dashboard-filter-option${selectedOverviewAdUnitId === adUnit._id ? " selected" : ""}`}
                              onClick={() => {
                                setSelectedOverviewAdUnitId(adUnit._id);
                                setAdUnitFilterSearch(adUnit.name || adUnit._id);
                                setIsAdUnitFilterOpen(false);
                              }}
                            >
                              {adUnit.name || adUnit._id}
                            </button>
                          ))
                        ) : (
                          <div className="dashboard-filter-option-empty">No ad units found.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <small className="dashboard-filter-helper">
                    {selectedOverviewAdUnit
                      ? `Filtering by ${selectedOverviewAdUnit.name || selectedOverviewAdUnit._id}`
                      : "Showing all ad units"}
                  </small>
                </div>
              )}
              <div
                className="dashboard-filter-field dashboard-filter-field-wide"
                ref={adChannelFilterRef}
              >
                <label
                  htmlFor="dashboard-ad-channel-filter"
                  className="account-list-label"
                >
                  Ad Channel
                </label>
                <div className="dashboard-filter-combobox">
                  <input
                    id="dashboard-ad-channel-filter"
                    type="text"
                    className="account-select dashboard-filter-select dashboard-filter-input"
                    placeholder="All Ad Channels"
                    value={adChannelFilterSearch}
                    onFocus={() => setIsAdChannelFilterOpen(true)}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setAdChannelFilterSearch(nextValue);
                      setIsAdChannelFilterOpen(true);
                      if (!nextValue.trim()) {
                        setSelectedOverviewAdChannelId("");
                        return;
                      }
                      const selectedLabel =
                        selectedOverviewAdChannel?.name ||
                        selectedOverviewAdChannel?._id ||
                        "";
                      if (
                        selectedOverviewAdChannelId &&
                        nextValue !== selectedLabel
                      ) {
                        setSelectedOverviewAdChannelId("");
                      }
                    }}
                  />
                  {isAdChannelFilterOpen && (
                    <div
                      className="dashboard-filter-options"
                      role="listbox"
                      aria-label="Ad Channel filter options"
                    >
                      <button
                        type="button"
                        className={`dashboard-filter-option${!selectedOverviewAdChannelId ? " selected" : ""}`}
                        onClick={() => {
                          setSelectedOverviewAdChannelId("");
                          setAdChannelFilterSearch("");
                          setIsAdChannelFilterOpen(false);
                        }}
                      >
                        All Ad Channels
                      </button>
                      {filteredAdChannelOptions.length > 0 ? (
                        filteredAdChannelOptions.map((adChannel) => (
                          <button
                            type="button"
                            key={adChannel._id}
                            className={`dashboard-filter-option${selectedOverviewAdChannelId === adChannel._id ? " selected" : ""}`}
                            onClick={() => {
                              setSelectedOverviewAdChannelId(adChannel._id);
                              setAdChannelFilterSearch(
                                adChannel.name || adChannel._id,
                              );
                              setIsAdChannelFilterOpen(false);
                            }}
                          >
                            {adChannel.name || adChannel._id}
                          </button>
                        ))
                      ) : (
                        <div className="dashboard-filter-option-empty">
                          No ad channels found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <small className="dashboard-filter-helper">
                  {selectedOverviewAdChannel
                    ? `Filtering by ${selectedOverviewAdChannel.name || selectedOverviewAdChannel._id}`
                    : "Showing all ad channels"}
                </small>
              </div>
            </div>
            <div className="dashboard-header-actions">
              <AccountSelector />
            </div>
          </div>
        )}
      </header>

      {isOverviewView && (
        <section className="dashboard-overview">
          {hasSearchNoDashboardData && (
            <p className="no-data">No dashboard data found for this search.</p>
          )}

          <div className="dashboard-overview-top-grid">
            <div className="dashboard-breakdown-panel">
              <div className="dashboard-breakdown-header">
                <div>
                  <h3>
                    {overviewMode === "campaign"
                      ? "Campaign Performance"
                      : "Ad Unit / Banner Performance"}
                  </h3>
                  <p>
                    Select an item to update its detailed daily graph.
                  </p>
                </div>
                <div className="dashboard-breakdown-header-actions">
                  <span>{overviewBreakdownItems.length} items</span>
                  {hasSelectedOverviewItem && (
                    <button
                      type="button"
                      className="dashboard-breakdown-reset"
                      onClick={handleViewAllOverviewItems}
                      aria-label={`View all ${overviewMode === "campaign" ? "campaigns" : "ad units"}`}
                      title="Clear selected item and view all"
                    >
                      <span aria-hidden="true">×</span>
                      View all
                    </button>
                  )}
                </div>
              </div>
              {analyticsLoading ? (
                <div className="no-data">Loading report items...</div>
              ) : overviewBreakdownItems.length === 0 ? (
                <div className="no-data">
                  No report items for the selected filters.
                </div>
              ) : (
                <div className="dashboard-breakdown-list">
                  {overviewBreakdownItems.map((item) => {
                    const itemId = String(
                      overviewMode === "campaign"
                        ? item.campaignId
                        : item.adUnitId,
                    );
                    const isSelected =
                      overviewMode === "campaign"
                        ? itemId === selectedOverviewCampaignId
                        : itemId === selectedOverviewAdUnitId;
                    return (
                      <button
                        type="button"
                        key={`${overviewMode}-${itemId}`}
                        className={`dashboard-breakdown-row${isSelected ? " is-selected" : ""}`}
                        onClick={() => {
                          if (overviewMode === "campaign") {
                            setSelectedOverviewCampaignId(itemId);
                            setCampaignFilterSearch(item.name || itemId);
                          } else {
                            setSelectedOverviewAdUnitId(itemId);
                            setAdUnitFilterSearch(item.name || itemId);
                          }
                        }}
                      >
                        <span className="dashboard-breakdown-identity">
                          <CampaignTablePreview
                            adUnit={item.previewAdUnit}
                            className="dashboard-breakdown-preview"
                            deferUntilVisible
                          />
                          <span className="dashboard-breakdown-copy">
                            <span className="dashboard-breakdown-name">
                              {item.name || item.adCode || "Unnamed item"}
                            </span>
                            {overviewMode === "adUnit" && (
                              <span className="dashboard-breakdown-caption">
                                Campaign: {item.campaignName || "Unavailable"}
                              </span>
                            )}
                          </span>
                        </span>
                        <span>
                          Impressions: {formatNumber(item.impressions)}
                        </span>
                        <span>Clicks: {formatNumber(item.clicks)}</span>
                        <span>CTR: {Number(item.ctr || 0).toFixed(2)}%</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <aside
              className="dashboard-kpi-grid dashboard-kpi-stack"
              aria-label="Overview totals"
            >
              <div className="stat-card dashboard-kpi-card">
                <h4>Impressions</h4>
                <p className="stat-value">
                  {analyticsLoading
                    ? "..."
                    : formatNumber(analytics.impressions)}
                </p>
              </div>
              <div className="stat-card dashboard-kpi-card">
                <h4>Clicks</h4>
                <p className="stat-value">
                  {analyticsLoading ? "..." : formatNumber(analytics.clicks)}
                </p>
              </div>
              <div className="stat-card dashboard-kpi-card">
                <h4>CTR</h4>
                <p className="stat-value">
                  {analyticsLoading
                    ? "..."
                    : `${Number(analytics.ctr || 0).toFixed(2)}%`}
                </p>
              </div>
            </aside>
          </div>

          <div className="dashboard-chart-panel">
            <div className="dashboard-chart-header">
              <div>
                <h3>Daily Performance</h3>
                <p>
                  Uses the existing analytics endpoint with your selected date
                  range and ad channel selection.
                </p>
              </div>
              <div className="dashboard-chart-meta">
                <span>
                  {analytics.topCampaigns[0]?.name
                    ? `Top Campaign: ${analytics.topCampaigns[0].name}`
                    : overviewMode === "adUnit" &&
                        selectedOverviewAdUnitCampaignName
                      ? `Top Campaign: ${selectedOverviewAdUnitCampaignName}`
                    : "No campaign data yet"}
                </span>
                <span>
                  {analytics.topAdUnits[0]?.name
                    ? `Top Ad Unit: ${analytics.topAdUnits[0].name}`
                    : "No ad unit data yet"}
                </span>
              </div>
            </div>
            {analyticsError && (
              <div className="dashboard-analytics-error" role="alert">
                <span>{analyticsError}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={fetchAnalytics}
                  disabled={analyticsLoading}
                >
                  {analyticsLoading ? "Retrying…" : "Retry"}
                </button>
              </div>
            )}
            <div className="dashboard-chart-canvas">
              {analyticsLoading ? (
                <div className="no-data">Loading analytics...</div>
              ) : analytics.daily.length > 0 ? (
                <Bar
                  data={analyticsChartData}
                  options={analyticsChartOptions}
                />
              ) : (
                <div className="no-data">
                  No analytics yet for this date range.
                </div>
              )}
            </div>
          </div>

          {selectedCampaignAdUnitDailyReports.length > 0 && (
            <section className="dashboard-ad-unit-daily-section">
              <div className="dashboard-ad-unit-daily-section-header">
                <div>
                  <h3>Ad Unit Daily Performance</h3>
                  <p>
                    Daily performance for every Ad Unit in {selectedOverviewCampaign.name}.
                  </p>
                </div>
                <span>{selectedCampaignAdUnitDailyReports.length} Ad Units</span>
              </div>
              <div className="dashboard-ad-unit-daily-grid">
                {selectedCampaignAdUnitDailyReports.map((report) => (
                  <article
                    key={report.adUnit._id}
                    className="dashboard-ad-unit-daily-panel"
                  >
                    <div className="dashboard-ad-unit-daily-header">
                      <div className="dashboard-ad-unit-daily-identity">
                        <CampaignTablePreview
                          adUnit={report.adUnit}
                          className="dashboard-ad-unit-daily-preview"
                          deferUntilVisible
                        />
                        <div>
                          <h4>{report.adUnit.name || report.adUnit.adCode}</h4>
                          <p>Daily Performance</p>
                        </div>
                      </div>
                      <div className="dashboard-ad-unit-daily-metrics">
                        <span>Impressions: {formatNumber(report.impressions)}</span>
                        <span>Clicks: {formatNumber(report.clicks)}</span>
                        <span>CTR: {report.ctr.toFixed(2)}%</span>
                      </div>
                    </div>
                    <div className="dashboard-ad-unit-daily-chart">
                      {analyticsLoading ? (
                        <div className="no-data">Loading analytics...</div>
                      ) : report.daily.length > 0 ? (
                        <Bar
                          data={buildDailyChartData(report.daily)}
                          options={analyticsChartOptions}
                        />
                      ) : (
                        <div className="no-data">
                          No analytics yet for this date range.
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

        </section>
      )}

      {isCampaignView && (
        <div className="main-content campaigns-main-content">
          <div className="campaigns-page">
            <div className="campaigns-section-panel">
              <div className="campaign-table-wrap">
                <table className="campaign-table">
                  <thead>
                    <tr>
                      {CAMPAIGN_TABLE_SORT_COLUMNS.map((column) => {
                        const isActiveSort = campaignSort.key === column.key;
                        return (
                          <th
                            key={column.key}
                            aria-sort={
                              isActiveSort
                                ? campaignSort.direction === "asc"
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <button
                              type="button"
                              className={`campaign-table-sort-button ${isActiveSort ? "is-active" : ""}`}
                              onClick={() =>
                                handleCampaignTableSort(column.key)
                              }
                            >
                              <span>{column.label}</span>
                              <span
                                className="campaign-table-sort-icon"
                                aria-hidden="true"
                              >
                                {isActiveSort
                                  ? campaignSort.direction === "asc"
                                    ? "▲"
                                    : "▼"
                                  : "↕"}
                              </span>
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCampaigns.map((campaign) => {
                      const campaignSearchRows = getCampaignTitleSearchRows(
                        campaign,
                        debouncedSearchQuery,
                      );
                      const campaignAdUnits = campaignSearchRows.adUnits;
                      const isExpanded =
                        expandedCampaignIds.has(campaign._id) ||
                        searchExpandedCampaignIds.has(campaign._id);

                      return (
                        <React.Fragment key={campaign._id}>
                          {campaignSearchRows.showCampaign && (
                            <tr
                              className={`campaign-table-row campaign-table-campaign-row ${isExpanded ? "is-expanded" : ""}`}
                              onClick={() => handleOpenCampaignEditor(campaign)}
                            >
                              <td>
                                <span>{formatTableDate(campaign.startDate)}</span>
                                <span className="campaign-table-end-date">
                                  {formatTableDate(campaign.endDate)}
                                </span>
                              </td>
                              <CampaignTableNameCell
                                campaign={campaign}
                                onOpen={handleOpenCampaignEditor}
                              />
                              <td>{formatNumber(campaign.totalImpressions)}</td>
                              <td>{formatNumber(campaign.impressionsToday)}</td>
                              <td>{formatNumber(campaign.totalClicks)}</td>
                              <td>{formatNumber(campaign.clicksToday)}</td>
                              <td>
                                <div className="campaign-table-row-end">
                                  <span>
                                    {Number(campaign.ctr || 0).toFixed(2)}%
                                  </span>
                                  <button
                                    type="button"
                                    className="campaign-table-expand-toggle"
                                    aria-expanded={isExpanded}
                                    aria-label={`${isExpanded ? "Hide" : "Show"} ad units for ${campaign.name}`}
                                    disabled={campaignAdUnits.length === 0}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (campaignAdUnits.length > 0) {
                                        toggleCampaignAdUnits(
                                          campaign._id,
                                          isExpanded,
                                        );
                                      }
                                    }}
                                  >
                                    {campaignAdUnits.length > 0 ? (
                                      <span aria-hidden="true">
                                        {isExpanded ? "−" : "+"}
                                      </span>
                                    ) : (
                                      <UiIcon name="minus" size={17} />
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                          {isExpanded &&
                            campaignAdUnits.map((adUnit) => (
                              <tr
                                key={`${campaign._id}-${adUnit._id}`}
                                className="campaign-table-row campaign-table-adunit-row"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleOpenEditAdUnitModal(
                                    adUnit,
                                    campaign._id,
                                  );
                                }}
                              >
                                <td>
                                  <span>
                                    {formatTableDate(adUnit.startDate)}
                                  </span>
                                  <span className="campaign-table-end-date">
                                    {formatTableDate(adUnit.endDate)}
                                  </span>
                                </td>
                                <AdUnitTableNameCell
                                  adUnit={adUnit}
                                  campaignId={campaign._id}
                                  onOpen={handleOpenEditAdUnitModal}
                                />
                                <td>{formatNumber(adUnit.impressions)}</td>
                                <td>{formatNumber(adUnit.impressionsToday)}</td>
                                <td>{formatNumber(adUnit.clicks)}</td>
                                <td>{formatNumber(adUnit.clicksToday)}</td>
                                <td>{Number(adUnit.ctr || 0).toFixed(2)}%</td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {campaignHasMore && (
                <div ref={loadMoreRef} className="campaign-list-loader">
                  {campaignsLoadingMore
                    ? "Loading more campaigns..."
                    : "Scroll to load more"}
                </div>
              )}
              {!campaignHasMore && campaigns.length > 0 && (
                <div className="campaign-list-loader">No more campaigns</div>
              )}
              {sortedCampaigns.length === 0 && (
                <p className="no-campaigns">
                  {normalizedSearchQuery
                    ? "No campaigns found."
                    : "No campaigns yet. Create one to get started!"}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {isCampaignView && (
        <Modal
          isOpen={Boolean(campaignEditorCampaign)}
          title={
            campaignEditorCampaign
              ? `Edit ${campaignEditorCampaign.name}`
              : "Edit Campaign"
          }
          onClose={handleCloseCampaignEditor}
          contentClassName="campaign-editor-modal"
          closeOnEscape={false}
          closeOnOverlay={false}
        >
          {campaignEditorCampaign && (
            <div className="campaign-editor-content">
              <div className="campaign-editor-actions">
                <button
                  className={`btn-icon btn-status ${effectiveCampaignEditorStatus}`}
                  disabled={
                    campaignMutationBusy
                  }
                  aria-busy={
                    campaignStatusUpdatingId === campaignEditorCampaign._id
                  }
                  onClick={() =>
                    handleToggleCampaignStatus(
                      campaignEditorCampaign._id,
                      effectiveCampaignEditorStatus,
                    )
                  }
                  title={
                    effectiveCampaignEditorStatus === "active"
                      ? "Pause"
                      : "Activate"
                  }
                  aria-label={
                    effectiveCampaignEditorStatus === "active"
                      ? "Pause campaign"
                      : "Activate campaign"
                  }
                >
                  <UiIcon
                    name={effectiveCampaignEditorStatus === "active" ? "pause" : "play"}
                  />
                </button>
                <button
                  className="btn-icon btn-edit"
                  type="button"
                  disabled={campaignMutationBusy}
                  aria-busy={
                    pendingCampaignAction?.type === "duplicate" &&
                    pendingCampaignAction?.id === campaignEditorCampaign._id
                  }
                  onClick={() =>
                    handleDuplicateCampaign(campaignEditorCampaign)
                  }
                  title="Duplicate"
                  aria-label="Duplicate campaign"
                >
                  <UiIcon name="duplicate" />
                </button>
                <button
                  className="btn-icon btn-delete"
                  type="button"
                  disabled={campaignMutationBusy}
                  aria-busy={
                    pendingCampaignAction?.type === "delete" &&
                    pendingCampaignAction?.id === campaignEditorCampaign._id
                  }
                  onClick={() =>
                    handleDeleteCampaign(campaignEditorCampaign._id)
                  }
                  title="Delete"
                  aria-label="Delete campaign"
                >
                  <UiIcon name="delete" />
                </button>
              </div>

              <CampaignForm
                campaign={campaignEditorCampaign}
                statusOverride={effectiveCampaignEditorStatus}
                saveRevision={campaignSaveRevision}
                onDirtyChange={setCampaignDraftDirty}
                submitting={campaignMutationBusy}
                onSubmit={handleSubmitCampaign}
                onCancel={handleCloseCampaignEditor}
                adUnitManagementContent={(
                  <div className="assignment-popup-content">
                    <div className="campaign-inline-adunit-header">
                      <div>
                        <strong>Campaign Ad Units</strong>
                        <span>Manage the Ad Units without leaving this editor.</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm new-ad-unit-button"
                        onClick={() =>
                          handleOpenCreateAdUnitFromCampaignEditor(
                            campaignEditorCampaign._id,
                          )
                        }
                      >
                        + New Ad Unit
                      </button>
                    </div>
                    <div
                      className={`campaign-editor-adunit-list ${(campaignEditorCampaign.adUnits || []).length > 20 ? "is-scrollable" : ""}`}
                    >
                      {sortAlphabetically(
                        campaignEditorCampaign.adUnits || [],
                      ).map((adUnit) => (
                        <div
                          key={adUnit._id}
                          className="campaign-editor-adunit-row"
                        >
                          <div className="campaign-editor-adunit-summary">
                            <strong>{adUnit.name || "Untitled Ad Unit"}</strong>
                            <span>{adUnit.status || "active"}</span>
                          </div>
                          <div className="campaign-editor-adunit-actions">
                            <button
                              className={`btn-icon btn-status ${adUnit.status}`}
                              type="button"
                              disabled={Boolean(pendingAdUnitAction)}
                              aria-busy={
                                pendingAdUnitAction?.type === "status" &&
                                pendingAdUnitAction?.id === adUnit._id
                              }
                              onClick={() =>
                                handleToggleAdUnitStatus(
                                  adUnit._id,
                                  adUnit.status,
                                )
                              }
                              title={
                                adUnit.status === "active"
                                  ? "Pause"
                                  : "Activate"
                              }
                              aria-label={
                                adUnit.status === "active"
                                  ? "Pause ad unit"
                                  : "Activate ad unit"
                              }
                            >
                              <UiIcon
                                name={
                                  adUnit.status === "active" ? "pause" : "play"
                                }
                              />
                            </button>
                            <button
                              className="btn-icon btn-edit"
                              type="button"
                              disabled={Boolean(pendingAdUnitAction)}
                              aria-busy={
                                pendingAdUnitAction?.type === "duplicate" &&
                                pendingAdUnitAction?.id === adUnit._id
                              }
                              onClick={() => handleDuplicateAdUnit(adUnit)}
                              title="Duplicate"
                              aria-label="Duplicate ad unit"
                            >
                              <UiIcon name="duplicate" />
                            </button>
                            <button
                              className="btn-icon btn-edit"
                              type="button"
                              disabled={Boolean(pendingAdUnitAction)}
                              aria-busy={
                                pendingAdUnitAction?.type === "load" &&
                                pendingAdUnitAction?.id === adUnit._id
                              }
                              onClick={() =>
                                handleOpenEditAdUnitModal(
                                  adUnit,
                                  campaignEditorCampaign._id,
                                  true,
                                )
                              }
                              title="Edit"
                              aria-label="Edit ad unit"
                            >
                              <UiIcon name="edit" />
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              type="button"
                              disabled={Boolean(pendingAdUnitAction)}
                              aria-busy={
                                pendingAdUnitAction?.type === "delete" &&
                                pendingAdUnitAction?.id === adUnit._id
                              }
                              onClick={() => handleDeleteAdUnit(adUnit._id)}
                              title="Delete"
                              aria-label="Delete ad unit"
                            >
                              <UiIcon name="delete" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {(campaignEditorCampaign.adUnits || []).length === 0 && (
                      <p className="no-data">
                        No ad units in this campaign.{" "}
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() =>
                            handleOpenCreateAdUnitFromCampaignEditor(
                              campaignEditorCampaign._id,
                            )
                          }
                        >
                          Create one
                        </button>
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          )}
        </Modal>
      )}

      {isCampaignView && (
        <Modal
          isOpen={showCampaignModal}
          title={editingCampaign ? "Edit Campaign" : "Create New Campaign"}
          onClose={handleCloseCampaignModal}
          contentClassName="campaign-editor-modal"
        >
          <CampaignForm
            campaign={editingCampaign}
            saveRevision={campaignSaveRevision}
            submitting={submitting}
            onSubmit={handleSubmitCampaign}
            onCancel={handleCloseCampaignModal}
          />
        </Modal>
      )}

      {isCampaignView && (
        <Modal
          isOpen={showAdUnitModal}
          title={editingAdUnit ? "Edit Ad Unit" : "Create New Ad Unit"}
          onClose={handleCloseAdUnitModal}
          contentClassName="campaign-editor-modal"
          closeOnEscape={!editingAdUnit}
          closeOnOverlay={!editingAdUnit}
        >
          <AdUnitForm
            adUnit={editingAdUnit}
            saveRevision={adUnitSaveRevision}
            submitting={submitting}
            campaignId={selectedCampaign}
            campaign={campaigns.find((c) => c._id === selectedCampaign)}
            onSubmit={handleSubmitAdUnit}
            onCancel={handleCloseAdUnitModal}
          />
        </Modal>
      )}
    </div>
  );
}

export default Dashboard;
